import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Briefcase, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATES, CHARGE_TYPES } from "@/data/legalData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function AttorneyJoin() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [f, setF] = useState({ name: "", firm: "", email: "", password: "", phone: "", website: "", years_experience: "", bio: "" });
  const [states, setStates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.password) { toast.error("Name, email and password are required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/attorney/register", {
        ...f, years_experience: f.years_experience ? Number(f.years_experience) : null,
        states, practice_areas: areas,
      });
      setUser(data.user);
      toast.success("Your practice is live!");
      navigate("/attorney");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create account"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Link to="/attorneys" className="inline-flex items-center gap-1.5 text-ink-500 hover:text-forest-800 text-sm mb-6"><ArrowLeft className="h-4 w-4" /> Back to directory</Link>
        <span className="grid place-items-center h-14 w-14 rounded-2xl bg-forest-100 text-forest-800"><Briefcase className="h-7 w-7" /></span>
        <h1 className="mt-4 font-serif text-4xl font-semibold text-forest-900">Create your attorney account</h1>
        <p className="mt-3 text-ink-700">Get listed instantly, receive client leads, and manage your membership from your own dashboard.</p>

        <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl border border-parchment-200 bg-white p-8">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-ink-700 mb-1 block">Full name *</Label><Input data-testid="join-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl border-parchment-200" /></div>
            <div><Label className="text-ink-700 mb-1 block">Firm</Label><Input value={f.firm} onChange={(e) => setF({ ...f, firm: e.target.value })} className="rounded-xl border-parchment-200" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-ink-700 mb-1 block">Email *</Label><Input data-testid="join-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-xl border-parchment-200" /></div>
            <div><Label className="text-ink-700 mb-1 block">Password *</Label><Input data-testid="join-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="rounded-xl border-parchment-200" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-ink-700 mb-1 block">Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="rounded-xl border-parchment-200" /></div>
            <div><Label className="text-ink-700 mb-1 block">Website</Label><Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="rounded-xl border-parchment-200" placeholder="https://" /></div>
          </div>
          <div><Label className="text-ink-700 mb-1 block">Years of experience</Label><Input type="number" min="0" value={f.years_experience} onChange={(e) => setF({ ...f, years_experience: e.target.value })} className="rounded-xl border-parchment-200 sm:w-40" /></div>

          <div>
            <Label className="text-ink-700 mb-2 block">States you practice in</Label>
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto thin-scroll p-1">
              {STATES.map((s) => (
                <button type="button" key={s.abbr} onClick={() => toggle(states, setStates, s.name)}
                  className={`rounded-full px-3 py-1 text-xs border transition-colors ${states.includes(s.name) ? "bg-forest-800 text-parchment-50 border-forest-800" : "bg-white border-parchment-200 text-ink-600 hover:border-forest-600/40"}`}>{s.name}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-ink-700 mb-2 block">Practice areas</Label>
            <div className="flex flex-wrap gap-2">
              {CHARGE_TYPES.map((c) => (
                <button type="button" key={c} onClick={() => toggle(areas, setAreas, c)}
                  className={`rounded-full px-3 py-1 text-xs border transition-colors ${areas.includes(c) ? "bg-terracotta text-white border-terracotta" : "bg-white border-parchment-200 text-ink-600 hover:border-forest-600/40"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div><Label className="text-ink-700 mb-1 block">Short bio</Label><Textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} rows={4} className="rounded-xl border-parchment-200 resize-none" placeholder="Tell potential clients about your experience and approach." /></div>

          <Button type="submit" data-testid="join-submit" disabled={loading} className="w-full rounded-xl h-11 bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account & get listed"}
          </Button>
          <p className="text-center text-xs text-ink-400">Listings start at $50/mo. Pick a plan on your dashboard after signup to go live.</p>
          <p data-testid="join-legal-consent" className="text-center text-xs text-ink-400">
            By joining you agree to our <Link to="/terms" className="underline hover:text-terracotta">Terms</Link>, <Link to="/privacy" className="underline hover:text-terracotta">Privacy Policy</Link> and <Link to="/disclosures" className="underline hover:text-terracotta">Attorney Directory Disclosures</Link>. You are responsible for complying with your state bar's advertising rules.
          </p>
        </form>
      </div>
    </div>
  );
}
